import ChevronLeftIcon from '@material-design-icons/svg/filled/chevron_left.svg?react';
import ChevronRightIcon from '@material-design-icons/svg/filled/chevron_right.svg?react';
import InboxIcon from '@material-design-icons/svg/filled/inbox.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { cn } from '../../../lib/utils';
import { RowSkeleton } from '../../management/ListShell';
import {
  HUBSPOT_TICKETS_PAGE_SIZE,
  type HubspotTicketDetail,
} from '../../../stores/hubspotManagementStore';
import { HubspotDappIcon } from '../HubspotDappIcon';
import {
  AssigneeAvatar,
  ChecksChip,
  getTicketDisplayName,
  getTicketVersionLabel,
  StatusChip,
  SubmissionKindChip,
  TicketAge,
} from '../table/HubspotTicketCells';

export interface HubspotTicketPagination {
  currentPage: number;
  totalPages: number;
  total: number;
}

interface HubspotTicketListProps {
  tickets: HubspotTicketDetail[];
  pagination: HubspotTicketPagination;
  isLoading: boolean;
  selectedTicketId: string | null;
  hasActiveFilters: boolean;
  onSelectTicket: (ticketId: string) => void;
  onPageChange: (page: number) => void;
  onReload: () => void;
  onClearFilters: () => void;
}

export function HubspotTicketsListHeader({
  total,
  isLoading,
  onReload,
}: {
  total: number;
  isLoading: boolean;
  onReload: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
      <h2 className="text-xs font-medium text-neutral-400">
        {isLoading && total === 0 ? (
          <span
            className="inline-block h-3 w-16 animate-pulse rounded bg-neutral-700/70 align-middle"
            aria-hidden="true"
          />
        ) : (
          <>
            <span className="text-white">{total}</span>{' '}
            {total === 1 ? 'ticket' : 'tickets'}
          </>
        )}
      </h2>
      <button
        type="button"
        onClick={onReload}
        disabled={isLoading}
        aria-label="Refresh tickets"
        title="Refresh"
        className="inline-flex size-7 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
      >
        <RefreshIcon
          className={cn('size-4 fill-current', isLoading && 'animate-spin')}
        />
      </button>
    </div>
  );
}

export function HubspotTicketsEmptyState({
  hasActiveFilters,
  onClearFilters,
}: {
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-neutral-800">
        <InboxIcon className="size-6 fill-current text-neutral-500" />
      </div>
      <div className="text-sm font-medium text-white">
        {hasActiveFilters ? 'No tickets match' : 'Queue is empty'}
      </div>
      <p className="max-w-xs text-xs text-neutral-400">
        {hasActiveFilters
          ? 'Try another segment or clear the filters.'
          : 'New submissions appear here as HubSpot syncs.'}
      </p>
      {hasActiveFilters && (
        <div className="text-xs">
          <button
            type="button"
            onClick={onClearFilters}
            className="rounded-md border border-neutral-700 px-3 py-1.5 font-medium text-neutral-200 transition-colors hover:bg-neutral-800"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}

export function HubspotTicketPager({
  pagination,
  onPageChange,
}: {
  pagination: HubspotTicketPagination;
  onPageChange: (page: number) => void;
}) {
  const { currentPage, totalPages, total } = pagination;

  if (total === 0) {
    return null;
  }

  const start = (currentPage - 1) * HUBSPOT_TICKETS_PAGE_SIZE + 1;
  const end = Math.min(currentPage * HUBSPOT_TICKETS_PAGE_SIZE, total);
  const pagerButtonClassName =
    'inline-flex size-8 items-center justify-center rounded-md text-neutral-300 transition-colors hover:bg-neutral-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div className="flex items-center justify-between gap-3 border-t border-neutral-800 px-3 py-1.5 text-xs text-neutral-400 sm:px-4">
      <span className="tabular-nums">
        {start}–{end} of {total}
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            className={pagerButtonClassName}
          >
            <ChevronLeftIcon className="size-5 fill-current" />
          </button>
          <span className="tabular-nums">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
            className={pagerButtonClassName}
          >
            <ChevronRightIcon className="size-5 fill-current" />
          </button>
        </div>
      )}
    </div>
  );
}

function HubspotTicketRow({
  ticket,
  isSelected,
  onSelect,
}: {
  ticket: HubspotTicketDetail;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const displayName = getTicketDisplayName(ticket);
  const version = getTicketVersionLabel(ticket);

  return (
    <button
      type="button"
      onClick={onSelect}
      data-ticket-id={ticket.id}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'flex w-full items-start gap-3 border-l-2 px-3 py-3 text-left transition-colors sm:px-4',
        isSelected
          ? 'border-teal-400 bg-teal-500/[0.08]'
          : 'border-transparent hover:bg-white/[0.03] active:bg-white/[0.05]',
      )}
    >
      <HubspotDappIcon
        iconUrl={ticket.tokenIconUrl}
        name={displayName}
        className="mt-0.5 size-10"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold text-white">
            {displayName}
          </span>
          <TicketAge
            date={ticket.createdAt}
            className="shrink-0 text-xs text-neutral-500"
          />
        </div>
        <div className="mt-0.5 truncate text-xs text-neutral-400">
          {version && <span>{version}</span>}
          {version && <span className="mx-1 text-neutral-600">·</span>}
          <span className="font-mono">#{ticket.hubspotId}</span>
          <span className="mx-1 text-neutral-600">·</span>
          <span>{ticket.submissionSource}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <StatusChip ticket={ticket} />
            <ChecksChip summary={ticket.automatedReviewSummary} />
            <SubmissionKindChip ticket={ticket} />
          </div>
          <AssigneeAvatar name={ticket.assignedUserName} />
        </div>
      </div>
    </button>
  );
}

/** Row-based queue used on phones and as the navigator beside an open ticket. */
export function HubspotTicketList({
  tickets,
  pagination,
  isLoading,
  selectedTicketId,
  hasActiveFilters,
  onSelectTicket,
  onPageChange,
  onReload,
  onClearFilters,
}: HubspotTicketListProps) {
  const isInitialLoading = isLoading && tickets.length === 0;

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900/60">
      <HubspotTicketsListHeader
        total={pagination.total}
        isLoading={isLoading}
        onReload={onReload}
      />
      <div
        className={cn(
          'divide-y divide-neutral-800/80 border-t border-neutral-800 transition-opacity',
          isLoading && tickets.length > 0 && 'opacity-60',
        )}
      >
        {isInitialLoading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <RowSkeleton key={index} />
          ))
        ) : tickets.length === 0 ? (
          <HubspotTicketsEmptyState
            hasActiveFilters={hasActiveFilters}
            onClearFilters={onClearFilters}
          />
        ) : (
          tickets.map((ticket) => (
            <HubspotTicketRow
              key={ticket.id}
              ticket={ticket}
              isSelected={ticket.id === selectedTicketId}
              onSelect={() => onSelectTicket(ticket.id)}
            />
          ))
        )}
      </div>
      <HubspotTicketPager pagination={pagination} onPageChange={onPageChange} />
    </div>
  );
}
